import React, { useMemo } from 'react';
import { getChampionSplashUrl } from '../../lib/champions';
import { log } from '../../lib/logger';

/**
 * APEX v7.4.5 — App Background
 *
 * Fond d'écran global de l'app : splash art d'un champion aléatoire.
 * Sélection en cascade :
 *   1. Parmi les Mains du pool (priorité)
 *   2. Fallback : parmi les Flex
 *   3. Fallback : null (pas de splash)
 *
 * V7.4.5 :
 * - Position fixed avec z-index 0 (le body est noir, splash visible par-dessus)
 * - Opacity 10% (au lieu de 8%) pour être bien visible
 * - Vignette équilibrée (visible mais pas écrasante)
 */
export default function AppBackground({ userSettings }) {
  const splashChampion = useMemo(() => {
    const pool = Array.isArray(userSettings?.champion_pool)
      ? userSettings.champion_pool
      : [];

    const mains = pool.filter(p => p && p.name && p.status === 'main');
    const flex = pool.filter(p => p && p.name && p.status === 'flex');
    const candidates = mains.length > 0 ? mains : flex;

    if (candidates.length === 0) {
      log.debug('[splash] Pas de candidats (mains:', mains.length, 'flex:', flex.length, ')');
      return null;
    }

    const sessionKey = 'apex_session_splash';
    let stored;
    try {
      stored = sessionStorage.getItem(sessionKey);
    } catch {
      stored = null;
    }

    if (stored && candidates.some(c => c.name === stored)) {
      log.debug('[APEX v7.4.5 splash] Réutilise session:', stored);
      return stored;
    }

    const pick = candidates[Math.floor(Math.random() * candidates.length)].name;
    try {
      sessionStorage.setItem(sessionKey, pick);
    } catch {
      /* ignore */
    }
    log.debug('[splash] Nouveau tirage:', pick, '(candidats:', candidates.map(c => c.name).join(', '), ')');
    return pick;
  }, [userSettings?.champion_pool]);

  if (!splashChampion) return null;

  const splashUrl = getChampionSplashUrl(splashChampion);

  return (
    <>
      {/* Splash art en background — z-0 (le body est noir, donc le splash est visible) */}
      <div
        className="fixed inset-0 bg-cover bg-center pointer-events-none"
        style={{
          backgroundImage: `url(${splashUrl})`,
          opacity: 0.10,
          transform: 'scale(1.1)',
          zIndex: 0
        }}
        aria-hidden="true"
      />
      {/* Vignette gradient (allégée) */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse at center, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.85) 100%)
          `,
          zIndex: 0
        }}
        aria-hidden="true"
      />
    </>
  );
}
